import { useRef, useCallback } from 'react'
import { size, keys, forEach } from 'lodash'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'
import { createTeamResourceContext } from './createTeamResourceContext'
import useNodeTasksResources from '../shared/resources/api/v1/admin/useNodeTasksResources'
import { isActiveTask, isRecentTask } from '../shared/utils/nodeTask/nodeTaskHelpers'
import { useTaskEvents } from '../shared/websocket'
import useThrottledCallback from '../shared/hooks/useThrottledCallback'

const { Provider: BaseNodeTasksProvider, useHook: useNodeTasks } = createTeamResourceContext({
  name: 'NodeTasks',
  useResources: useNodeTasksResources,
  responseKey: 'nodeTasks',
  lookups: [{ key: 'nodeTasksById', field: 'id' }],
  groupings: [{ key: 'nodeTasksByServiceId', field: 'dockerServiceId' }],
  computed: [{
    key: 'activeTaskByServiceId',
    fn: (nodeTasks) => nodeTasks.reduce((result, task) => {
      if (!task.dockerServiceId) return result
      if (result[task.dockerServiceId]) return result
      if (isActiveTask(task) && isRecentTask(task)) {
        result[task.dockerServiceId] = task
      }
      return result
    }, {}),
  }],
  polling: false,
})

// Component that subscribes to task events and triggers reload
function TaskEventSubscriber({ children }) {
  const { reload } = useNodeTasks()
  const { enqueueSnackbar } = useSnackbar()
  const { formatMessage } = useIntl()
  const taskStatusRef = useRef({})
  const throttledReload = useThrottledCallback(reload)

  const MAX_TRACKED_TASKS = 200

  const handleTaskUpdate = useCallback((task) => {
    throttledReload()

    const previousStatus = taskStatusRef.current[task.id]
    const currentStatus = task.status

    // Only notify if status actually changed
    if (previousStatus !== currentStatus) {
      taskStatusRef.current[task.id] = currentStatus

      // Prevent unbounded growth: trim oldest entries when limit is exceeded
      if (size(taskStatusRef.current) > MAX_TRACKED_TASKS) {
        const idsToRemove = keys(taskStatusRef.current).slice(0, MAX_TRACKED_TASKS / 2)
        forEach(idsToRemove, (id) => { delete taskStatusRef.current[id] })
      }

      const translatedType = formatMessage({ id: `tasks.type.${task.taskType}` })

      if (currentStatus === 'completed') {
        enqueueSnackbar(formatMessage({ id: 'tasks.notifications.completed' }, { type: translatedType }), { variant: 'success' })
      } else if (currentStatus === 'failed') {
        enqueueSnackbar(formatMessage({ id: 'tasks.notifications.failed' }, { type: translatedType }), { variant: 'error' })
      } else if (currentStatus === 'running') {
        enqueueSnackbar(formatMessage({ id: 'tasks.notifications.running' }, { type: translatedType }), { variant: 'info' })
      }
    }
  }, [throttledReload, enqueueSnackbar, formatMessage])

  useTaskEvents({
    onCreated: throttledReload,
    onUpdated: handleTaskUpdate,
  })

  return children
}

// Wrapper provider that adds WebSocket integration
function NodeTasksProvider({ children }) {
  return (
    <BaseNodeTasksProvider>
      <TaskEventSubscriber>
        {children}
      </TaskEventSubscriber>
    </BaseNodeTasksProvider>
  )
}

export { NodeTasksProvider, useNodeTasks }
